import React, { useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AISecurityChat from '../../components/chat/AISecurityChat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Shield, 
  Lock, 
  Eye, 
  Database, 
  FileCheck, 
  AlertCircle,
  CheckCircle2,
  ShieldCheck,
  Scale,
  Globe,
  Server,
  Key,
  UserCheck,
  Clock
} from 'lucide-react';

export default function TrustCenter() {
  const [activeSection, setActiveSection] = useState('security');
  const [chatOpen, setChatOpen] = useState(false);

  const sections = [
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'privacy', label: 'Privacy', icon: Lock },
    { id: 'compliance', label: 'Compliance', icon: FileCheck },
    { id: 'data', label: 'Data Usage', icon: Database },
  ];

  const securityFeatures = [
    {
      icon: Lock,
      title: 'End-to-End Encryption',
      description: 'All data transmitted between your browser and our servers is encrypted using TLS 1.3',
      status: 'active'
    },
    {
      icon: Key,
      title: 'API Key Security',
      description: 'API keys are hashed and stored securely. Rate limiting prevents abuse.',
      status: 'active'
    },
    {
      icon: UserCheck,
      title: 'Authentication',
      description: 'JWT-based authentication with httpOnly cookies for session security',
      status: 'active'
    },
    {
      icon: Server,
      title: 'Infrastructure',
      description: 'Hosted on secure cloud infrastructure with 99.9% uptime SLA',
      status: 'active'
    }
  ];

  const privacyPolicies = [
    {
      title: 'Data Collection',
      description: 'We collect only the data necessary to provide our services: entity information, corporate structure, and compliance data.'
    },
    {
      title: 'Data Retention',
      description: 'Your data is retained as long as your account is active. You can request deletion at any time.'
    },
    {
      title: 'Third-Party Sharing',
      description: 'We do not sell your data. Credit intelligence integrations (D&B, Nav) are used only for score calculations.'
    },
    {
      title: 'User Rights',
      description: 'You have the right to access, modify, export, or delete your data at any time.'
    }
  ];

  const complianceStandards = [
    { name: 'SOC 2 Type II', status: 'In Progress', description: 'Security and availability controls' },
    { name: 'GDPR', status: 'Compliant', description: 'EU data protection regulation' },
    { name: 'CCPA', status: 'Compliant', description: 'California Consumer Privacy Act' },
    { name: 'ISO 27001', status: 'Planned', description: 'Information security management' }
  ];

  const dataUsage = [
    {
      category: 'Corporate Structures',
      purpose: 'Store and analyze your entity structures for compliance scoring',
      retention: 'Active account + 30 days after deletion',
      shared: false
    },
    {
      category: 'Credit Intelligence Data',
      purpose: 'Calculate fundability scores and match with lenders',
      retention: '90 days',
      shared: 'Only with authorized credit platforms (D&B, Nav) for score calculation'
    },
    {
      category: 'API Usage Metrics',
      purpose: 'Track API calls for billing and rate limiting',
      retention: '12 months',
      shared: 'With Stripe for billing purposes only'
    },
    {
      category: 'Audit Logs',
      purpose: 'Security and compliance monitoring',
      retention: '24 months',
      shared: false
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-blue-400" />
            Trust Center
          </h1>
          <p className="text-slate-400 mt-2">
            Transparency into our security, privacy, and compliance practices
          </p>
        </div>

        {/* Legal Disclaimer Banner */}
        <Card className="bg-gradient-to-r from-amber-900/20 to-orange-900/20 border-amber-700/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-amber-200 mb-2">Important Legal Disclaimer</h3>
                <p className="text-sm text-amber-100/80 leading-relaxed">
                  CorpCraft provides corporate structure automation and credit intelligence tools for informational purposes only. 
                  Fundability scores and recommendations are estimates based on available data and should not be considered financial, 
                  legal, or tax advice. Always consult with qualified professionals before making business decisions. 
                  Credit scores from third-party platforms (D&B, Nav) may differ from actual scores provided by those platforms. 
                  We do not guarantee loan approval or specific outcomes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section Tabs */}
        <div className="flex gap-2 border-b border-slate-800">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 ${
                activeSection === section.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              <section.icon className="w-4 h-4" />
              {section.label}
            </button>
          ))}
        </div>

        {/* Security Section */}
        {activeSection === 'security' && (
          <div className="space-y-4">
            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle>Security Infrastructure</CardTitle>
                <CardDescription>
                  Our commitment to protecting your data and infrastructure
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {securityFeatures.map((feature, index) => (
                    <div 
                      key={index}
                      className="p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-blue-500/10 rounded-lg">
                          <feature.icon className="w-5 h-5 text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-white">{feature.title}</h4>
                            <Badge variant="outline" className="text-green-400 border-green-400">
                              {feature.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-400">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle>Security Practices</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {[
                    'Regular security audits and penetration testing',
                    'Automated vulnerability scanning',
                    'Secure coding practices and code reviews',
                    'Incident response plan and 24/7 monitoring',
                    'Employee security training and background checks',
                    'Multi-factor authentication support (coming soon)'
                  ].map((practice, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300">{practice}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Privacy Section */}
        {activeSection === 'privacy' && (
          <div className="space-y-4">
            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle>Privacy Policies</CardTitle>
                <CardDescription>
                  How we collect, use, and protect your personal information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {privacyPolicies.map((policy, index) => (
                    <div 
                      key={index}
                      className="p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                    >
                      <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                        <Eye className="w-4 h-4 text-blue-400" />
                        {policy.title}
                      </h4>
                      <p className="text-sm text-slate-400">{policy.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle>Your Privacy Rights</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { icon: FileCheck, text: 'Request a copy of your data' },
                    { icon: Lock, text: 'Update or correct your data' },
                    { icon: AlertCircle, text: 'Object to data processing' },
                    { icon: Database, text: 'Request data deletion' }
                  ].map((right, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                      <right.icon className="w-5 h-5 text-blue-400" />
                      <span className="text-sm text-slate-300">{right.text}</span>
                    </div>
                  ))}
                </div>
                <Button onClick={() => setChatOpen(true)} className="mt-4" variant="outline">
                  Contact Privacy Team
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Compliance Section */}
        {activeSection === 'compliance' && (
          <Card className="bg-slate-900 border-slate-700">
            <CardHeader>
              <CardTitle>Compliance Standards</CardTitle>
              <CardDescription>
                Certifications and regulatory compliance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {complianceStandards.map((standard, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-blue-500/10 rounded-lg">
                        <Scale className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">{standard.name}</h4>
                        <p className="text-sm text-slate-400">{standard.description}</p>
                      </div>
                    </div>
                    <Badge 
                      variant={standard.status === 'Compliant' ? 'default' : 'outline'}
                      className={
                        standard.status === 'Compliant' 
                          ? 'bg-green-500/20 text-green-400 border-green-400' 
                          : standard.status === 'In Progress'
                          ? 'bg-yellow-500/20 text-yellow-400 border-yellow-400'
                          : ''
                      }
                    >
                      {standard.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Usage Section */}
        {activeSection === 'data' && (
          <Card className="bg-slate-900 border-slate-700">
            <CardHeader>
              <CardTitle>Data Usage & Retention</CardTitle>
              <CardDescription>
                What data we collect and how we use it
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dataUsage.map((item, index) => (
                  <div 
                    key={index}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                  >
                    <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <Database className="w-4 h-4 text-blue-400" />
                      {item.category}
                    </h4>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="text-slate-500 min-w-[100px]">Purpose:</span>
                        <span className="text-slate-300">{item.purpose}</span>
                      </div>
                      
                      <div className="flex items-start gap-2">
                        <Clock className="w-4 h-4 text-slate-500 mt-0.5" />
                        <span className="text-slate-500 min-w-[90px]">Retention:</span>
                        <span className="text-slate-300">{item.retention}</span>
                      </div>
                      
                      <div className="flex items-start gap-2">
                        {item.shared ? (
                          <Globe className="w-4 h-4 text-amber-400 mt-0.5" />
                        ) : (
                          <Lock className="w-4 h-4 text-green-400 mt-0.5" />
                        )}
                        <span className="text-slate-500 min-w-[90px]">Shared:</span>
                        <span className="text-slate-300">
                          {item.shared || 'Not shared with third parties'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Section */}
        <Card className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border-blue-700/50">
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="font-semibold text-white mb-2">Questions About Security or Privacy?</h3>
              <p className="text-sm text-slate-400 mb-4">
                Our security team is here to help
              </p>
              <Button onClick={() => setChatOpen(true)}>Contact Security Team</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Security Chat Modal */}
      <AISecurityChat isOpen={chatOpen} onClose={() => setChatOpen(false)} />
    </DashboardLayout>
  );
}
